import { useMemo } from 'react';
import type { ReactNode } from 'react';
import styled from '@emotion/styled';

import { tokens, Dropdown as DSDropdown, Icon, Stack, Text } from '@hotjar/hotsauce';
import type { IconName } from '@hotjar/hotsauce';

export interface ItemProps {
  icon?: IconName;
  isSelected?: boolean;
  isDisabled?: boolean;
  title: string | ReactNode;
  subTitle?: string | ReactNode;
  onClick: () => void;
}

const StyledButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  ${tokens.text('body2')};
  align-items: flex-start;
  padding: ${tokens.spacer.x2};
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  color: ${tokens.color.text_default};
  min-width: 270px;

  &:hover {
    background: ${tokens.color.neutral_100};
  }

  &:focus {
    background: ${tokens.color.ui_background};
    box-shadow: 0 0 0 1px ${tokens.color.ui_background}, 0 0 0 3px ${tokens.color.focus};
    transform: scale(1);
    z-index: 2;
  }

  &[disabled] {
    cursor: not-allowed;
    background-color: transparent;
    color: ${tokens.color.text_disabled};
  }

  ${({ isSelected }) => isSelected && `background-color: ${tokens.color.bg_active}`};
`;

const StyledIcon = styled(Icon)`
  margin-top: 2px;
  margin-right: ${tokens.spacer.x1};
`;

const Labels = styled(Stack)`
  align-items: flex-start;
  flex-grow: 1;
  text-align: left;
`;

const StyledSelectedIcon = styled.span`
  flex-shrink: 1;
  margin-left: ${tokens.spacer.x1};
  display: inline-flex;
  align-self: center;
`;

export const Dropdown = styled(DSDropdown)`
  .tippy-content {
    padding: ${tokens.spacer.x1} 0;
  }
`;

export const List = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Item = ({
  icon,
  title,
  subTitle,
  isSelected,
  isDisabled,
  onClick,
}: ItemProps): JSX.Element => {
  const isSelectedIconVisible = useMemo(() => isSelected && !isDisabled, [isSelected, isDisabled]);

  return (
    <li>
      <StyledButton isSelected={Boolean(isSelected)} disabled={isDisabled} onClick={onClick}>
        {icon && <StyledIcon icon={icon} />}
        <Labels direction="column" spacing="x1">
          <Text textColor={isDisabled ? 'text_disabled' : 'text_default'}>{title}</Text>
          {subTitle && (
            <Text textType="caption" textColor={isDisabled ? 'text_disabled' : 'text_light'}>
              {subTitle}
            </Text>
          )}
        </Labels>
        {isSelectedIconVisible && (
          <StyledSelectedIcon data-testid="dropdown-item-selected-icon">
            <Icon icon="selected" size="m" />
          </StyledSelectedIcon>
        )}
      </StyledButton>
    </li>
  );
};
